import { IEspmPropertyTypeCategoryNode } from "../../../services/zeos-api/contracts/EspmPropertyType";

export type EspmFlatOptionKind = "category" | "detail";

export interface IEspmFlatOption {
  key: string;
  kind: EspmFlatOptionKind;
  categoryId: number;
  detailId?: number;
  label: string;
  meta?: string;
}

export interface IEspmTypeaheadValue {
  categoryId?: number;
  detailId?: number;
}

/** Flat category + subtype options for Quick Screening; excludes Mixed Use. */
export function buildEspmFlatOptions(
  categories: IEspmPropertyTypeCategoryNode[]
): IEspmFlatOption[] {
  const options: IEspmFlatOption[] = [];

  for (const category of categories) {
    if (category.usesAllocation) {
      continue;
    }

    options.push({
      key: `cat-${category.espmPropertyTypeCategoryId}`,
      kind: "category",
      categoryId: category.espmPropertyTypeCategoryId,
      label: category.itemName,
    });

    for (const detail of category.details ?? []) {
      options.push({
        key: `det-${detail.espmPropertyTypeDetailId}`,
        kind: "detail",
        categoryId: category.espmPropertyTypeCategoryId,
        detailId: detail.espmPropertyTypeDetailId,
        label: detail.itemName,
        meta: category.itemName,
      });
    }
  }

  return options;
}

export function findEspmFlatOption(
  options: IEspmFlatOption[],
  value?: IEspmTypeaheadValue | null
): IEspmFlatOption | undefined {
  if (!value?.categoryId) {
    return undefined;
  }
  if (value.detailId != null) {
    return options.find(
      (o) =>
        o.kind === "detail" &&
        o.detailId === value.detailId &&
        o.categoryId === value.categoryId
    );
  }
  return options.find(
    (o) => o.kind === "category" && o.categoryId === value.categoryId
  );
}

export function formatEspmSelectionLabel(
  option: IEspmFlatOption | undefined
): string {
  return option?.label ?? "";
}

export function resolveEspmSelectionFromIds(
  categories: IEspmPropertyTypeCategoryNode[],
  categoryId?: number | null,
  detailId?: number | null
): { categoryId?: number; detailId?: number; propertyType: string } {
  if (categoryId == null) {
    return { propertyType: "" };
  }

  const category = categories.find(
    (c) => c.espmPropertyTypeCategoryId === categoryId && !c.usesAllocation
  );
  if (!category) {
    return { propertyType: "" };
  }

  if (detailId != null) {
    const detail = category.details?.find(
      (d) => d.espmPropertyTypeDetailId === detailId
    );
    if (detail) {
      return {
        categoryId,
        detailId,
        propertyType: detail.itemName,
      };
    }
  }

  return {
    categoryId,
    propertyType: category.itemName,
  };
}
